import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, BookOpen } from 'lucide-react';
import { howToGuides } from '../data/plantsData';

const HowToGuides = () => {
  const [expandedGuide, setExpandedGuide] = useState(null);

  const toggleGuide = (id) => {
    setExpandedGuide(expandedGuide === id ? null : id);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="guides" className="py-20 bg-white text-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center justify-center mb-4">
            <BookOpen className="h-8 w-8 text-primary-500 mr-3" />
            <h2 className="text-4xl font-bold text-gray-900">
              How-To Guides
            </h2>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Step-by-step tutorials to help you master plant care and create thriving green spaces in your home.
          </p>
        </motion.div>

        <motion.div
          className="space-y-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {howToGuides.map((guide) => (
            <motion.div
              key={guide.id}
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
              variants={cardVariants}
            >
              <motion.div
                className="p-6 cursor-pointer flex items-center justify-between"
                onClick={() => toggleGuide(guide.id)}
                whileHover={{ backgroundColor: 'rgba(16, 185, 129, 0.05)' }}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-4xl text-primary-500">{guide.icon}</div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {guide.title}
                    </h3>
                    <p className="text-gray-600">
                      {guide.description}
                    </p>
                  </div>
                </div>
                <motion.div
                  animate={{ rotate: expandedGuide === guide.id ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown className="h-6 w-6 text-primary-500" />
                </motion.div>
              </motion.div>

              <AnimatePresence>
                {expandedGuide === guide.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 border-t border-gray-200">
                      <div className="pt-6">
                        <h4 className="text-lg font-semibold text-gray-800 mb-4">
                          Step-by-Step Instructions:
                        </h4>
                        <div className="space-y-4">
                          {guide.steps.map((step, index) => (
                            <motion.div
                              key={index}
                              className="flex items-start space-x-4"
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-primary-500 to-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-lg">
                                {index + 1}
                              </div>
                              <p className="text-gray-700 pt-1">{step}</p>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <motion.button
            className="group bg-primary-600 hover:bg-primary-700 text-white border border-primary-600 hover:border-primary-700 px-8 py-3 rounded-full font-semibold transition-all duration-300 flex items-center space-x-2 mx-auto transform hover:scale-105 shadow-md hover:shadow-lg"
            whileTap={{ scale: 0.95 }}
          >
            <span>View More Guides</span>
            <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default HowToGuides;
